import { register, trackEvent } from "../../analytics.mjs";
import { FETCH_SESSION_INFO, LOG_IN, MAYBE_FETCH_SESSION_INFO, MAYBE_LOG_OUT, REGISTER, REQUEST_HUMAN_STATUS, REQUEST_RESET_PASSWORD } from "./actionTypes.mjs";
import { beginLoadingSessionInfo, beginLogIn, beginRegister, beginRequestHumanStatus, beginRequestPasswordReset, errorLogIn, errorRegister, errorRequestHumanStatus, errorRequestPasswordReset, errorSessionInfo, fetchSessionInfo, loggedOut, receiveSessionInfo, successLogIn, successRegister, successRequestHumanStatus, successRequestPasswordReset } from "./actions.mjs";
import { infoIsLoaded, loggedIn } from "./selectors.mjs";
import { all, call, put, select, takeLatest } from "redux-saga/lib/effects.js";
//#region src/store/session/saga.ts
var SessionSaga = class {
	sessionApi;
	constructor(sessionApi) {
		this.sessionApi = sessionApi;
		Object.getOwnPropertyNames(Object.getPrototypeOf(this)).filter((key) => key !== "constructor").forEach((key) => {
			this[key] = this[key].bind(this);
		});
	}
	*maybeFetchSessionInfo() {
		if (!(yield select(infoIsLoaded))) {
			yield put(beginLoadingSessionInfo());
			yield this.fetchSessionInfo();
		}
	}
	*fetchSessionInfo() {
		try {
			const session = yield call(this.sessionApi.fetchSessionInfo);
			const human = session && session.human;
			register({ email: human && human.email });
			yield put(receiveSessionInfo(session));
		} catch (e) {
			yield put(errorSessionInfo(e.message));
		}
	}
	*logIn(action) {
		const { email, password, googleIDToken, facebookIDToken } = action.payload;
		try {
			yield put(beginLogIn());
			const logInResponse = yield call(this.sessionApi.logIn, email, password, googleIDToken, facebookIDToken);
			if (logInResponse && logInResponse.error) {
				yield put(errorLogIn(logInResponse.error));
				trackEvent("session", "login", "success-with-error", {
					email,
					error: logInResponse.error
				});
			} else {
				yield put(fetchSessionInfo());
				yield put(successLogIn(logInResponse.redirect_to));
			}
			trackEvent("session", "login", "success", { email });
		} catch (e) {
			const response = e.response;
			const errMessage = e.message;
			if (response && response.error) trackEvent("session", "login", "error-with-response", {
				email,
				error: errMessage
			});
			yield put(errorLogIn(errMessage, response && response.status));
			trackEvent("session", "login", "error", {
				email,
				error: e.message || response && response.status
			});
		}
	}
	parseHumanStatus = (statusResponse) => {
		return {
			hasAutoGeneratedPassword: !!(statusResponse && statusResponse.has_generated_password),
			isNewHuman: statusResponse === null
		};
	};
	*maybeResetPassword(payload, trackEvent) {
		if (payload.shouldResetPassword) {
			yield call(this.sessionApi.requestResetPassword, payload.email);
			trackEvent("reset-password", payload);
		}
	}
	*requestPasswordReset(action) {
		const { email } = action.payload;
		const trackEvent$1 = (label, opt) => trackEvent("session", "reset-password", label, opt);
		try {
			yield put(beginRequestPasswordReset());
			const statusResponse = yield call(this.sessionApi.requestHumanStatus, email);
			const humanStatus = this.parseHumanStatus(statusResponse);
			const payload = {
				email,
				...humanStatus
			};
			const { hasAutoGeneratedPassword, isNewHuman } = humanStatus;
			yield call(this.maybeResetPassword, {
				...payload,
				shouldResetPassword: !isNewHuman
			}, trackEvent$1);
			yield put(successRequestPasswordReset(email, hasAutoGeneratedPassword, isNewHuman));
			trackEvent$1(isNewHuman ? "no-account" : "success", payload);
		} catch (e) {
			const response = e.response;
			const error = e.message;
			if (response) {
				if (response && response.error) {
					yield put(errorRequestPasswordReset(error, response.status));
					trackEvent$1("error", {
						email,
						error
					});
					return;
				}
			}
			yield put(successRequestPasswordReset(email, false, true));
			trackEvent$1("no-account", { email });
			return;
		}
	}
	*requestHumanStatus(action) {
		const { email } = action.payload;
		const trackEvent$2 = (label, opt) => trackEvent("session", "request-human-status", label, opt);
		try {
			yield put(beginRequestHumanStatus());
			const statusResponse = yield call(this.sessionApi.requestHumanStatus, email);
			const humanStatus = this.parseHumanStatus(statusResponse);
			const payload = {
				email,
				...humanStatus
			};
			const { hasAutoGeneratedPassword, isNewHuman } = humanStatus;
			yield put(successRequestHumanStatus(email, hasAutoGeneratedPassword, isNewHuman));
			trackEvent$2(isNewHuman ? "no-account" : "success", payload);
		} catch (e) {
			const response = e.response;
			const error = e.message;
			if (response && response.error) {
				yield put(errorRequestHumanStatus(error, response.status));
				trackEvent$2("error", {
					email,
					error
				});
				return;
			}
			yield put(successRequestHumanStatus(email, false, true));
			trackEvent$2("no-account", { email });
			return;
		}
	}
	*register(action) {
		const { email, name, password, phone, googleIDToken, facebookIDToken, passwordlessToken, hasGeneratedPassword, redirectUrl } = action.payload;
		try {
			yield put(beginRegister());
			const registerResponse = yield call(this.sessionApi.register, email, name, password, phone, googleIDToken, facebookIDToken, passwordlessToken, hasGeneratedPassword, redirectUrl);
			if (registerResponse && registerResponse.error) throw new Error(registerResponse.error);
			if (registerResponse && registerResponse.errors) {
				const errorField = Object.keys(registerResponse.errors)[0];
				const errorsArray = registerResponse.errors[errorField];
				if (Array.isArray(errorsArray)) throw Error(`That ${errorField.replace("primary_phone", "phone number").replace("_", " ")} ${errorsArray[0]}.`);
			}
			yield put(fetchSessionInfo());
			yield put(successRegister(registerResponse?.redirect_to));
			trackEvent("session", "register", "success", { email });
		} catch (e) {
			yield put(errorRegister(e.message));
			trackEvent("session", "register", "error", {
				email,
				error: e.message
			});
		}
	}
	*maybeLogOut() {
		try {
			if (yield select(loggedIn)) yield call(this.sessionApi.logOut);
			yield put(loggedOut());
		} catch (e) {
			yield put(errorSessionInfo(e.message));
		}
	}
	*saga() {
		yield all([
			takeLatest(MAYBE_FETCH_SESSION_INFO, this.maybeFetchSessionInfo),
			takeLatest(FETCH_SESSION_INFO, this.fetchSessionInfo),
			takeLatest(LOG_IN, this.logIn),
			takeLatest(REGISTER, this.register),
			takeLatest(MAYBE_LOG_OUT, this.maybeLogOut),
			takeLatest(REQUEST_HUMAN_STATUS, this.requestHumanStatus),
			takeLatest(REQUEST_RESET_PASSWORD, this.requestPasswordReset)
		]);
	}
};
//#endregion
export { SessionSaga, SessionSaga as default };

//# sourceMappingURL=saga.mjs.map